/**
 * Defensive readers — the seam between a Firestore document and an engine type.
 *
 * A document is untyped `unknown` until something checks it. These four readers do the
 * checking in one place, so a surface never guards a field itself and a garbage record
 * fails loudly at the read rather than quietly three screens later. They are **readers,
 * not validators**: they refuse a record whose *required* shape is wrong, and they drop
 * (rather than repair) optional fields that are the wrong type — a stray `note: 42` on a
 * primitive is not a reason to hide a parent's character.
 *
 * `TimestampLike` is structural, so a Firestore `Timestamp` passes through unchanged and
 * a plain `{ seconds, nanoseconds }` from an export does too.
 */
import { MalformedRecordError } from './errors.js';
const PRIMITIVE_KINDS = ['character', 'setting', 'prop', 'beat'];
const SCOPES = ['global', 'author'];
const STATUSES = ['pending', 'generating', 'ready', 'error'];
const LAYER_ROLES = ['backdrop', 'character', 'prop', 'foreground'];
const FLAT_KINDS = ['composite', 'harmonized'];
const ESTABLISHMENTS = ['single-pose', 'model-sheet'];
function asDoc(value, what, id) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new MalformedRecordError(what, id, 'the document is not an object');
    }
    return value;
}
function fail(what, id, detail) {
    throw new MalformedRecordError(what, id, detail);
}
function optionalString(value) {
    return typeof value === 'string' && value !== '' ? value : undefined;
}
function optionalBoolean(value) {
    return typeof value === 'boolean' ? value : undefined;
}
function optionalNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
function optionalStrings(value) {
    if (!Array.isArray(value))
        return undefined;
    return value.filter((item) => typeof item === 'string');
}
/** A Firestore `Timestamp` matches structurally; `null` (a pending server stamp) is kept. */
function optionalTimestamp(value) {
    if (value === null)
        return null;
    if (typeof value !== 'object' || value === undefined)
        return undefined;
    const candidate = value;
    if (typeof candidate.seconds !== 'number' || typeof candidate.nanoseconds !== 'number') {
        return undefined;
    }
    return value;
}
/** `en` is required; a missing `en` means the string is not usable and is dropped. */
function optionalLocalized(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const candidate = value;
    if (typeof candidate.en !== 'string')
        return undefined;
    const tr = optionalString(candidate.tr);
    return tr === undefined ? { en: candidate.en } : { en: candidate.en, tr };
}
function requiredLocalized(value, what, id, field) {
    const localized = optionalLocalized(value);
    if (localized === undefined)
        fail(what, id, `${field} is not a localized string`);
    return localized;
}
function oneOf(value, allowed, what, id, field) {
    if (typeof value === 'string' && allowed.includes(value))
        return value;
    fail(what, id, `${field} is not one of ${allowed.join(' | ')}: ${JSON.stringify(value)}`);
}
function optionalOneOf(value, allowed) {
    return typeof value === 'string' && allowed.includes(value)
        ? value
        : undefined;
}
/** A variant is a flat `Record<string, string>`; anything else in it is dropped. */
function readVariant(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return {};
    const variant = {};
    for (const [key, option] of Object.entries(value)) {
        if (typeof option === 'string')
            variant[key] = option;
    }
    return variant;
}
function readAxes(value) {
    if (!Array.isArray(value))
        return [];
    const axes = [];
    for (const entry of value) {
        if (typeof entry !== 'object' || entry === null)
            continue;
        const axis = entry;
        const key = optionalString(axis['key']);
        const label = optionalLocalized(axis['label']);
        if (key === undefined || label === undefined)
            continue;
        const options = [];
        if (Array.isArray(axis['options'])) {
            for (const raw of axis['options']) {
                if (typeof raw !== 'object' || raw === null)
                    continue;
                const option = raw;
                const optionKey = optionalString(option['key']);
                const optionLabel = optionalLocalized(option['label']);
                if (optionKey !== undefined && optionLabel !== undefined) {
                    options.push({ key: optionKey, label: optionLabel });
                }
            }
        }
        axes.push({ key, label, options });
    }
    return axes;
}
function readTransform(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const transform = value;
    const x = optionalNumber(transform['x']);
    const y = optionalNumber(transform['y']);
    const scale = optionalNumber(transform['scale']);
    if (x === undefined || y === undefined || scale === undefined)
        return undefined;
    return { x, y, scale };
}
/** A frame ratio must be two positive integers; anything else is dropped (→ the base frame). */
function readFrame(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const frame = value;
    const w = optionalNumber(frame['w']);
    const h = optionalNumber(frame['h']);
    if (w === undefined || h === undefined)
        return undefined;
    if (!Number.isInteger(w) || !Number.isInteger(h) || w <= 0 || h <= 0)
        return undefined;
    return { w, h };
}
function readPrimitiveRef(value, what, id, field) {
    const ref = asDoc(value, what, id);
    const primitiveId = optionalString(ref['primitiveId']);
    const version = optionalNumber(ref['version']);
    if (primitiveId === undefined)
        fail(what, id, `${field}.primitiveId is missing`);
    if (version === undefined)
        fail(what, id, `${field}.version is missing`);
    const transform = readTransform(ref['transform']);
    return transform === undefined
        ? { primitiveId, version, variant: readVariant(ref['variant']) }
        : { primitiveId, version, variant: readVariant(ref['variant']), transform };
}
function readMotion(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const motion = value;
    const camera = optionalOneOf(motion['camera'], [
        'hold',
        'push-in',
        'pull-back',
        'pan-left',
        'pan-right',
    ]);
    const durationMs = optionalNumber(motion['durationMs']);
    const transitionOut = optionalOneOf(motion['transitionOut'], [
        'cut',
        'dissolve',
        'slide',
    ]);
    if (camera === undefined || durationMs === undefined || transitionOut === undefined) {
        return undefined;
    }
    return { camera, durationMs, transitionOut };
}
/**
 * A scene's setting, which may not have been chosen yet.
 *
 * A story is written while it is being composed: a parent adds a scene, then a setting, then
 * who is in it. So a scene with an **unset** setting (`primitiveId: ''`) is a legal, ordinary
 * state — not a malformed record — and refusing it would make a story unreadable the moment
 * someone added a row to it. The composer will not compose one, which is where that rule
 * belongs.
 *
 * Characters and props stay strict: they only exist because someone deliberately placed them.
 */
function readSettingRef(value, what, id, field) {
    const ref = asDoc(value, what, id);
    if (ref['primitiveId'] === '' || ref['primitiveId'] === undefined) {
        return { primitiveId: '', version: 0, variant: {} };
    }
    return readPrimitiveRef(value, what, id, field);
}
function readScene(value, what, id, index) {
    const scene = asDoc(value, what, id);
    const field = `scenes[${index}]`;
    const characters = [];
    if (Array.isArray(scene['characters'])) {
        scene['characters'].forEach((raw, i) => {
            const ref = readPrimitiveRef(raw, what, id, `${field}.characters[${i}]`);
            const place = optionalString(raw['place']);
            characters.push(place === undefined ? ref : { ...ref, place });
        });
    }
    const out = {
        setting: readSettingRef(scene['setting'], what, id, `${field}.setting`),
        characters,
    };
    if (Array.isArray(scene['props'])) {
        out.props = scene['props'].map((raw, i) => readPrimitiveRef(raw, what, id, `${field}.props[${i}]`));
    }
    const beat = scene['beat'];
    if (typeof beat === 'object' && beat !== null) {
        const beatId = optionalString(beat['primitiveId']);
        const beatVersion = optionalNumber(beat['version']);
        if (beatId !== undefined && beatVersion !== undefined) {
            out.beat = { primitiveId: beatId, version: beatVersion };
        }
    }
    // A stored `motion` on a story scene is a pre-adaptation record (PRD 02 §13.1 moved it to
    // the `AdaptedScene`). Stripped on read, not repaired: the story is format-neutral now, and
    // nothing has ever consumed the field, so there is nothing to carry across.
    const frame = readFrame(scene['frame']);
    if (frame !== undefined)
        out.frame = frame;
    const line = optionalLocalized(scene['line']);
    if (line !== undefined)
        out.line = line;
    const renderId = optionalString(scene['renderId']);
    if (renderId !== undefined)
        out.renderId = renderId;
    return out;
}
// ─── the four record readers ─────────────────────────────────────────────────
/**
 * A `Primitive` from its document. `id` is the document id, not a field, because Firestore
 * keeps it outside the data and a record that disagrees with its own path is a bug.
 */
export function normalizePrimitive(id, data) {
    const what = 'primitive';
    const doc = asDoc(data, what, id);
    const primitive = {
        id,
        kind: oneOf(doc['kind'], PRIMITIVE_KINDS, what, id, 'kind'),
        scope: oneOf(doc['scope'], SCOPES, what, id, 'scope'),
        ownerId: optionalString(doc['ownerId']) ?? fail(what, id, 'ownerId is missing'),
        name: requiredLocalized(doc['name'], what, id, 'name'),
        axes: readAxes(doc['axes']),
        version: optionalNumber(doc['version']) ?? fail(what, id, 'version is missing'),
        createdAt: optionalTimestamp(doc['createdAt']) ?? null,
        updatedAt: optionalTimestamp(doc['updatedAt']) ?? null,
        createdBy: optionalString(doc['createdBy']) ?? fail(what, id, 'createdBy is missing'),
    };
    const forkedFrom = optionalString(doc['forkedFrom']);
    if (forkedFrom !== undefined)
        primitive.forkedFrom = forkedFrom;
    const description = optionalLocalized(doc['description']);
    if (description !== undefined)
        primitive.description = description;
    const identityNotes = optionalLocalized(doc['identityNotes']);
    if (identityNotes !== undefined)
        primitive.identityNotes = identityNotes;
    const referenceDrawingPath = optionalString(doc['referenceDrawingPath']);
    if (referenceDrawingPath !== undefined)
        primitive.referenceDrawingPath = referenceDrawingPath;
    const colourAnchor = optionalLocalized(doc['colourAnchor']);
    if (colourAnchor !== undefined)
        primitive.colourAnchor = colourAnchor;
    const establishment = optionalOneOf(doc['establishment'], ESTABLISHMENTS);
    if (establishment !== undefined)
        primitive.establishment = establishment;
    const cast = optionalStrings(doc['cast']);
    if (cast !== undefined)
        primitive.cast = cast;
    const archivedAt = optionalTimestamp(doc['archivedAt']);
    if (archivedAt !== undefined)
        primitive.archivedAt = archivedAt;
    const archivedBy = optionalString(doc['archivedBy']);
    if (archivedBy !== undefined)
        primitive.archivedBy = archivedBy;
    return primitive;
}
/** A `Story` from its document. A story with no readable scene is still a story. */
export function normalizeStory(id, data) {
    const what = 'story';
    const doc = asDoc(data, what, id);
    const scenes = Array.isArray(doc['scenes'])
        ? doc['scenes'].map((scene, index) => readScene(scene, what, id, index))
        : [];
    const story = {
        id,
        ownerId: optionalString(doc['ownerId']) ?? fail(what, id, 'ownerId is missing'),
        title: requiredLocalized(doc['title'], what, id, 'title'),
        themeId: optionalString(doc['themeId']) ?? fail(what, id, 'themeId is missing'),
        scenes,
        createdAt: optionalTimestamp(doc['createdAt']) ?? null,
        updatedAt: optionalTimestamp(doc['updatedAt']) ?? null,
    };
    const narrative = optionalLocalized(doc['narrative']);
    if (narrative !== undefined)
        story.narrative = narrative;
    // A cover choice is a beat index. Anything else (negative, fractional, past the end) is
    // dropped rather than repaired: the Studio then falls back to its default, and the story
    // stays readable.
    const coverBeat = doc['coverBeat'];
    if (typeof coverBeat === 'number' &&
        Number.isInteger(coverBeat) &&
        coverBeat >= 0 &&
        coverBeat < scenes.length) {
        story.coverBeat = coverBeat;
    }
    const archivedAt = optionalTimestamp(doc['archivedAt']);
    if (archivedAt !== undefined)
        story.archivedAt = archivedAt;
    const archivedBy = optionalString(doc['archivedBy']);
    if (archivedBy !== undefined)
        story.archivedBy = archivedBy;
    return story;
}
function readSheetSplit(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const split = value;
    const expected = optionalNumber(split['expected']);
    const usable = optionalNumber(split['usable']);
    if (expected === undefined || usable === undefined)
        return undefined;
    const skipped = [];
    if (Array.isArray(split['skipped'])) {
        for (const raw of split['skipped']) {
            if (typeof raw !== 'object' || raw === null)
                continue;
            const variantKey = optionalString(raw['variantKey']);
            const reason = optionalString(raw['reason']);
            if (variantKey !== undefined && reason !== undefined)
                skipped.push({ variantKey, reason });
        }
    }
    const summary = {
        expected,
        usable,
        populated: optionalStrings(split['populated']) ?? [],
        skipped,
    };
    const remakeRecommended = optionalBoolean(split['remakeRecommended']);
    if (remakeRecommended !== undefined)
        summary.remakeRecommended = remakeRecommended;
    const remakeReason = optionalString(split['remakeReason']);
    if (remakeReason !== undefined)
        summary.remakeReason = remakeReason;
    return summary;
}
/**
 * A `VariantInstance` from its document — the record the whole cache turns on.
 *
 * A missing `status` is a hard failure: a record whose state cannot be read would show a
 * parent a picture that may not exist, or spend a generation that already ran.
 */
export function normalizeVariantInstance(id, data) {
    const what = 'variantInstance';
    const doc = asDoc(data, what, id);
    const instance = {
        id,
        primitiveId: optionalString(doc['primitiveId']) ?? fail(what, id, 'primitiveId is missing'),
        primitiveVersion: optionalNumber(doc['primitiveVersion']) ?? fail(what, id, 'primitiveVersion is missing'),
        themeId: optionalString(doc['themeId']) ?? fail(what, id, 'themeId is missing'),
        variant: readVariant(doc['variant']),
        status: oneOf(doc['status'], STATUSES, what, id, 'status'),
        createdAt: optionalTimestamp(doc['createdAt']) ?? null,
    };
    const assetPath = optionalString(doc['assetPath']);
    if (assetPath !== undefined)
        instance.assetPath = assetPath;
    const isBaseReference = optionalBoolean(doc['isBaseReference']);
    if (isBaseReference !== undefined)
        instance.isBaseReference = isBaseReference;
    const sheetAssetPath = optionalString(doc['sheetAssetPath']);
    if (sheetAssetPath !== undefined)
        instance.sheetAssetPath = sheetAssetPath;
    const sheetSplit = readSheetSplit(doc['sheetSplit']);
    if (sheetSplit !== undefined)
        instance.sheetSplit = sheetSplit;
    const derived = doc['derivedFromSheet'];
    if (typeof derived === 'object' && derived !== null) {
        const instanceId = optionalString(derived['instanceId']);
        const cell = optionalNumber(derived['cell']);
        if (instanceId !== undefined && cell !== undefined) {
            instance.derivedFromSheet = { instanceId, cell };
        }
    }
    const primitivePath = optionalString(doc['primitivePath']);
    if (primitivePath !== undefined)
        instance.primitivePath = primitivePath;
    const requestedBy = optionalString(doc['requestedBy']);
    if (requestedBy !== undefined)
        instance.requestedBy = requestedBy;
    const approved = optionalBoolean(doc['approved']);
    if (approved !== undefined)
        instance.approved = approved;
    const approvedAt = optionalTimestamp(doc['approvedAt']);
    if (approvedAt !== undefined)
        instance.approvedAt = approvedAt;
    const approvedBy = optionalString(doc['approvedBy']);
    if (approvedBy !== undefined)
        instance.approvedBy = approvedBy;
    const error = optionalString(doc['error']);
    if (error !== undefined)
        instance.error = error;
    const attempts = optionalNumber(doc['attempts']);
    if (attempts !== undefined)
        instance.attempts = attempts;
    const claimedAt = optionalTimestamp(doc['claimedAt']);
    if (claimedAt !== undefined)
        instance.claimedAt = claimedAt;
    const claimToken = optionalString(doc['claimToken']);
    if (claimToken !== undefined)
        instance.claimToken = claimToken;
    return instance;
}
function readLayers(value) {
    if (!Array.isArray(value))
        return [];
    const layers = [];
    for (const raw of value) {
        if (typeof raw !== 'object' || raw === null)
            continue;
        const layer = raw;
        const role = optionalOneOf(layer['role'], LAYER_ROLES);
        const instanceId = optionalString(layer['instanceId']);
        const assetPath = optionalString(layer['assetPath']);
        if (role === undefined || instanceId === undefined || assetPath === undefined)
            continue;
        const transform = readTransform(layer['transform']);
        const z = optionalNumber(layer['transform']?.['z']);
        layers.push(transform !== undefined && z !== undefined
            ? { role, instanceId, assetPath, transform: { ...transform, z } }
            : { role, instanceId, assetPath });
    }
    return layers;
}
function readHarmonize(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const harmonize = value;
    const status = optionalOneOf(harmonize['status'], STATUSES);
    if (status === undefined)
        return undefined;
    const state = { status };
    const attempts = optionalNumber(harmonize['attempts']);
    if (attempts !== undefined)
        state.attempts = attempts;
    const claimedAt = optionalTimestamp(harmonize['claimedAt']);
    if (claimedAt !== undefined)
        state.claimedAt = claimedAt;
    const claimToken = optionalString(harmonize['claimToken']);
    if (claimToken !== undefined)
        state.claimToken = claimToken;
    const error = optionalString(harmonize['error']);
    if (error !== undefined)
        state.error = error;
    const requestedBy = optionalString(harmonize['requestedBy']);
    if (requestedBy !== undefined)
        state.requestedBy = requestedBy;
    return state;
}
/** A `SceneRender` from its document. */
export function normalizeSceneRender(id, data) {
    const what = 'sceneRender';
    const doc = asDoc(data, what, id);
    const render = {
        id,
        themeId: optionalString(doc['themeId']) ?? fail(what, id, 'themeId is missing'),
        status: oneOf(doc['status'], STATUSES, what, id, 'status'),
        layers: readLayers(doc['layers']),
        usedInstances: optionalStrings(doc['usedInstances']) ?? [],
    };
    const flatAssetPath = optionalString(doc['flatAssetPath']);
    if (flatAssetPath !== undefined)
        render.flatAssetPath = flatAssetPath;
    const flatKind = optionalOneOf(doc['flatKind'], FLAT_KINDS);
    if (flatKind !== undefined)
        render.flatKind = flatKind;
    const compositeAssetPath = optionalString(doc['compositeAssetPath']);
    if (compositeAssetPath !== undefined)
        render.compositeAssetPath = compositeAssetPath;
    const harmonize = readHarmonize(doc['harmonize']);
    if (harmonize !== undefined)
        render.harmonize = harmonize;
    if (doc['scene'] !== undefined)
        render.scene = readScene(doc['scene'], what, id, 0);
    const requestedBy = optionalString(doc['requestedBy']);
    if (requestedBy !== undefined)
        render.requestedBy = requestedBy;
    const waitingOn = optionalStrings(doc['waitingOn']);
    if (waitingOn !== undefined)
        render.waitingOn = waitingOn;
    const attempts = optionalNumber(doc['attempts']);
    if (attempts !== undefined)
        render.attempts = attempts;
    const claimedAt = optionalTimestamp(doc['claimedAt']);
    if (claimedAt !== undefined)
        render.claimedAt = claimedAt;
    const claimToken = optionalString(doc['claimToken']);
    if (claimToken !== undefined)
        render.claimToken = claimToken;
    const error = optionalString(doc['error']);
    if (error !== undefined)
        render.error = error;
    const createdAt = optionalTimestamp(doc['createdAt']);
    if (createdAt !== undefined)
        render.createdAt = createdAt;
    return render;
}
// ─── the adaptation reader ───────────────────────────────────────────────────
function readAdaptedScene(value, what, id, index) {
    const adapted = asDoc(value, what, id);
    const out = {};
    const baseIndex = optionalNumber(adapted['baseIndex']);
    if (baseIndex !== undefined && Number.isInteger(baseIndex) && baseIndex >= 0) {
        out.baseIndex = baseIndex;
    }
    // An override is a whole composition, read with the same strictness as a story's scene.
    if (adapted['scene'] !== undefined && adapted['scene'] !== null) {
        out.scene = readScene(adapted['scene'], what, id, index);
    }
    const baseHash = optionalString(adapted['baseHash']);
    if (baseHash !== undefined)
        out.baseHash = baseHash;
    // Film only, and this is the one place it may live (PRD 02 §13.1).
    const motion = readMotion(adapted['motion']);
    if (motion !== undefined)
        out.motion = motion;
    return out;
}
/**
 * An `Adaptation` from its document.
 *
 * The four ids are required — an adaptation that cannot say which story it derives from, or
 * which medium it is for, is not one. Its scenes are read strictly: an override that is not a
 * scene is a malformed record, because rendering it would invent a picture.
 */
export function normalizeAdaptation(id, data) {
    const what = 'adaptation';
    const doc = asDoc(data, what, id);
    const scenes = Array.isArray(doc['scenes'])
        ? doc['scenes'].map((scene, index) => readAdaptedScene(scene, what, id, index))
        : [];
    return {
        id,
        ownerId: optionalString(doc['ownerId']) ?? fail(what, id, 'ownerId is missing'),
        storyId: optionalString(doc['storyId']) ?? fail(what, id, 'storyId is missing'),
        mediumId: optionalString(doc['mediumId']) ?? fail(what, id, 'mediumId is missing'),
        themeId: optionalString(doc['themeId']) ?? fail(what, id, 'themeId is missing'),
        scenes,
        createdAt: optionalTimestamp(doc['createdAt']) ?? null,
        updatedAt: optionalTimestamp(doc['updatedAt']) ?? null,
    };
}
//# sourceMappingURL=normalize.js.map